import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Dialog } from 'primereact/dialog';
import { Badge } from 'primereact/badge';

import { defaultPageRowOptions, defaultRowOptions, paginatorLinks, showingEntries, SkeletonbodyTemplate, Skeletonitems } from '../../appconfig/Settings';
import AppBreadcrumb from '../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails, postAllData } from '../../service/ProductService';
import { LIST_DISPATCH, APPROVE_REJECT_DISPATCH } from '../../config/ApiConstant';
import { formatDateTime } from '../../appconfig/AppHelper';

export const DispatchList = () => {
    document.title = 'Dispatch List | Karan Enterprise';

    const navigate = useNavigate();
    const toast = useRef<any>(null);

    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [dispatchList, setDispatchList] = useState<any>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<any>([]);
    const [confirmDialogVisible, setConfirmDialogVisible] = useState<boolean>(false);
    const [confirmAction, setConfirmAction] = useState<string>('');
    const [selectedDispatch, setSelectedDispatch] = useState<any>(null);
    const [actionLoading, setActionLoading] = useState<boolean>(false);

    useEffect(() => {
        getDispatchList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const getDispatchList = async () => {
        try {
            setPageLoad(false);
            const response = await getAllDetails(LIST_DISPATCH);
            if (response) {
                setDispatchList(response?.data || []);
                setPageLoad(true);
            }
        } catch (error) {
            setPageLoad(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Something went wrong, Please try again.'
            });
        }
    };

    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
            </>
        );
    };

    const openConfirmDialog = (rowData: any, action: string) => {
        setSelectedDispatch(rowData);
        setConfirmAction(action);
        setConfirmDialogVisible(true);
    };

    const closeConfirmDialog = () => {
        setConfirmDialogVisible(false);
        setConfirmAction('');
        setSelectedDispatch(null);
    };

    const handleConfirmAction = async () => {
        try {
            setActionLoading(true);

            const requestData = {
                id: selectedDispatch.id,
                status: confirmAction
            };

            const response = await postAllData(APPROVE_REJECT_DISPATCH, requestData);

            if (response) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: response.message || `Dispatch ${confirmAction} successfully`
                });
                closeConfirmDialog();
                getDispatchList();
            }
        } catch (error: any) {
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: error.response?.data?.message || 'Something went wrong'
            });
        } finally {
            setActionLoading(false);
        }
    };

    const partyNameTemplate = (rowData: any) => {
        return <span className="font-bold">{rowData?.party?.party_name || '-'}</span>;
    };

    const createdByTemplate = (rowData: any) => {
        return (
            <span>
                {rowData?.created_by?.first_name} {rowData?.created_by?.last_name}
            </span>
        );
    };

    const statusTemplate = (rowData: any) => {
        const severity = rowData?.status === 'Approved' ? 'success' : rowData?.status === 'Rejected' ? 'danger' : 'warning';
        return <Badge severity={severity} value={rowData?.status?.toUpperCase()} />;
    };

    const dispatchDateTemplate = (rowData: any) => {
        return <span>{formatDateTime(rowData?.dispatch_date)}</span>;
    };

    const itemsCountTemplate = (rowData: any) => {
        return <span className="text-primary">{rowData?.items?.length || 0}</span>;
    };

    const actionBodyTemplate = (rowData: any) => {
        if (rowData?.status === 'Pending') {
            return (
                <div className="actions">
                    <Button icon="pi pi-check" tooltip="Approve" tooltipOptions={{ position: 'top' }} className="p-button-rounded p-button-success mr-2" onClick={() => openConfirmDialog(rowData, 'approved')} />
                    <Button icon="pi pi-times" tooltip="Reject" tooltipOptions={{ position: 'top' }} className="p-button-rounded p-button-danger" onClick={() => openConfirmDialog(rowData, 'rejected')} />
                </div>
            );
        }
        return <span className="text-gray-500">-</span>;
    };

    const rowExpansionTemplate = (data: any) => {
        return (
            <div className="p-3 surface-50 border-round">
                <h5 className="mb-3">Items for Dispatch #{data?.id}</h5>
                {data?.items && data?.items.length > 0 ? (
                    <DataTable value={data.items} stripedRows>
                        <Column field="roll.roll_id" header="Roll ID" sortable body={(rowData: any) => rowData?.roll?.roll_id || '-'} />
                        <Column field="roll.product.name" header="Product Name" sortable body={(rowData: any) => rowData?.roll?.product?.name || '-'} />
                        <Column field="roll.width" header="Width" sortable body={(rowData: any) => rowData?.roll?.width || '-'} />
                        <Column field="roll.length" header="Length" sortable body={(rowData: any) => rowData?.roll?.length || '-'} />
                        <Column field="roll.micron_gsm" header="Micron/GSM" sortable body={(rowData: any) => rowData?.roll?.micron_gsm || '-'} />
                        <Column
                            field="roll.is_owr"
                            header="OWR"
                            body={(rowData: any) => {
                                const isOwr = rowData?.roll?.is_owr === 1;
                                return <Badge severity={isOwr ? 'success' : 'danger'} value={isOwr ? 'Yes' : 'No'} />;
                            }}
                        />
                        <Column
                            field="roll.without_glase"
                            header="Without Glase"
                            body={(rowData: any) => {
                                const isWithoutGlase = rowData?.roll?.without_glase === 1;
                                return <Badge severity={isWithoutGlase ? 'success' : 'danger'} value={isWithoutGlase ? 'Yes' : 'No'} />;
                            }}
                        />
                    </DataTable>
                ) : (
                    <p className="text-gray-500">No items found</p>
                )}
            </div>
        );
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'Dispatch'} pageName={'Dispatch List'} pageTitle={'Dispatch List'} />
            <div className="grid crud-demo expanded-table">
                <div className="col-12">
                    <div className="card">
                        <Toast ref={toast} />
                        <Toolbar left={leftToolbarTemplate} right={() => (
                                <Button label="New Dispatch" icon="pi pi-plus" className="p-button-primary" onClick={() => navigate('/create-dispatch')} />
                            )}></Toolbar>
                        {pageLoad ? (
                            <>
                                <DataTable
                                    className="datatable-responsive"
                                    value={dispatchList}
                                    paginator={dispatchList.length >= 10 && true}
                                    globalFilter={globalFilter}
                                    rows={defaultRowOptions}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    emptyMessage="No Dispatch Data Found"
                                    showGridlines
                                    stripedRows
                                    expandedRows={expandedRows}
                                    onRowToggle={(e) => setExpandedRows(e.data)}
                                    rowExpansionTemplate={rowExpansionTemplate}
                                    dataKey="id"
                                >
                                    <Column expander style={{ width: '3rem' }} />
                                    <Column field="party_name" header="Party Name" sortable filter body={partyNameTemplate} />
                                    <Column field="dispatch_date" header="Dispatch Date" sortable filter body={dispatchDateTemplate} />
                                    <Column field="items" header="Items Count" sortable body={itemsCountTemplate} />
                                    <Column field="created_by" header="Created By" sortable body={createdByTemplate} />
                                    <Column field="status" header="Status" sortable filter body={statusTemplate} />
                                    <Column field="action" header="Action" body={actionBodyTemplate} />
                                </DataTable>
                            </>
                        ) : (
                            <>
                                <DataTable value={Skeletonitems}>
                                    <Column field="id" header="ID" body={SkeletonbodyTemplate} />
                                    <Column field="party_name" header="Party Name" body={SkeletonbodyTemplate} />
                                    <Column field="dispatch_date" header="Dispatch Date" body={SkeletonbodyTemplate} />
                                    <Column field="items" header="Items Count" body={SkeletonbodyTemplate} />
                                    <Column field="created_by" header="Created By" body={SkeletonbodyTemplate} />
                                    <Column field="status" header="Status" body={SkeletonbodyTemplate} />
                                    <Column field="action" header="Action" body={SkeletonbodyTemplate} />
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                visible={confirmDialogVisible}
                style={{ width: '450px' }}
                header="Confirm Action"
                modal
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary mr-2" onClick={closeConfirmDialog} disabled={actionLoading} />
                        <Button label={confirmAction === 'approved' ? 'Approve' : 'Reject'} icon="pi pi-check" loading={actionLoading} onClick={handleConfirmAction} className={confirmAction === 'approved' ? 'p-button-success' : 'p-button-danger'} />
                    </>
                }
                onHide={closeConfirmDialog}
            >
                <div className="flex align-items-center justify-content-center">
                    <i className={`pi ${confirmAction === 'approved' ? 'pi-check-circle' : 'pi-times-circle'} mr-3`} style={{ fontSize: '2rem', color: confirmAction === 'approved' ? '#22c55e' : '#ef4444' }} />
                    <span>Are you sure you want to {confirmAction} this dispatch request?</span>
                </div>
            </Dialog>
        </>
    );
};
